import { useLanguage } from "@/context/language"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { DividerV2 } from "@opencode-ai/ui/v2/divider-v2"
import { Field } from "@opencode-ai/ui/v2/field-v2"
import { TextInputV2 } from "@opencode-ai/ui/v2/text-input-v2"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { For, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function DialogCoworkProjectForm(props: {
  title: string
  initialName?: string
  submitLabel: string
  onSubmit: (name: string) => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ name: props.initialName ?? "" })
  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    const name = store.name.trim()
    if (!name) return
    dialog.close()
    props.onSubmit(name)
  }

  return (
    <Dialog fit>
      <form class="contents" onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <DividerV2 />
        <DialogBody class="flex w-96 flex-col gap-4 px-4 pt-4 pb-1">
          <Field>
            <Field.Label>{language.t("cowork.project.name")}</Field.Label>
            <TextInputV2
              autofocus
              appearance="large"
              class="!w-full"
              value={store.name}
              onInput={(event) => setStore("name", event.currentTarget.value)}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </ButtonV2>
          <ButtonV2 type="submit" variant="contrast" disabled={!store.name.trim()}>
            {props.submitLabel}
          </ButtonV2>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

export function DialogCoworkDeleteProject(props: { name: string; onDelete: () => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const remove = () => {
    dialog.close()
    props.onDelete()
  }

  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("cowork.project.deleteTitle")}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t("cowork.project.deleteConfirm", { name: props.name })}
        </p>
        <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.project.deleteDescription")}</p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={remove}>
          {language.t("common.delete")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogCoworkDeleteCompany(props: { name: string; onDelete: () => void }) {
  return (
    <DialogCoworkDeleteResource
      title="cowork.company.deleteTitle"
      confirmation="cowork.company.deleteConfirm"
      description="cowork.company.deleteDescription"
      name={props.name}
      onDelete={props.onDelete}
    />
  )
}

export function DialogCoworkDeleteEmployee(props: { name: string; onDelete: () => void }) {
  return (
    <DialogCoworkDeleteResource
      title="cowork.enterprise.employees.deleteTitle"
      confirmation="cowork.enterprise.employees.deleteConfirm"
      description="cowork.enterprise.employees.deleteDescription"
      name={props.name}
      onDelete={props.onDelete}
    />
  )
}

export function DialogCoworkDeleteReport(props: { name: string; onDelete: () => void }) {
  return (
    <DialogCoworkDeleteResource
      title="cowork.report.deleteTitle"
      confirmation="cowork.report.deleteConfirm"
      description="cowork.report.deleteDescription"
      name={props.name}
      onDelete={props.onDelete}
    />
  )
}

function DialogCoworkDeleteResource(props: {
  title: keyof typeof import("@/i18n/en").dict
  confirmation: keyof typeof import("@/i18n/en").dict
  description: keyof typeof import("@/i18n/en").dict
  name: string
  onDelete: () => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const remove = () => {
    dialog.close()
    props.onDelete()
  }
  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t(props.title)}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t(props.confirmation, { name: props.name })}
        </p>
        <p class="text-12-regular text-v2-text-text-muted">{language.t(props.description)}</p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={remove}>
          {language.t("common.delete")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogCoworkDeleteChat(props: { name: string; onDelete: () => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const remove = () => {
    dialog.close()
    props.onDelete()
  }

  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("cowork.chat.deleteTitle")}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t("cowork.chat.deleteConfirm", { name: props.name })}
        </p>
        <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.chat.deleteDescription")}</p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={remove}>
          {language.t("cowork.chat.delete")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogCoworkLogout(props: { user: string; onLogout: () => void }) {
  const language = useLanguage()
  const dialog = useDialog()
  const logout = () => {
    dialog.close()
    props.onLogout()
  }

  return (
    <Dialog fit>
      <DialogHeader>
        <DialogTitle>{language.t("cowork.profile.logoutTitle")}</DialogTitle>
      </DialogHeader>
      <DividerV2 />
      <DialogBody class="flex w-96 flex-col gap-2 px-4 pt-4 pb-1">
        <p class="text-14-regular text-v2-text-text-base">
          {language.t("cowork.profile.logoutConfirm", { user: props.user })}
        </p>
        <p class="text-12-regular text-v2-text-text-muted">{language.t("cowork.profile.logoutDescription")}</p>
      </DialogBody>
      <DialogFooter>
        <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </ButtonV2>
        <ButtonV2 type="button" variant="danger" onClick={logout}>
          {language.t("cowork.profile.logout")}
        </ButtonV2>
      </DialogFooter>
    </Dialog>
  )
}

export function DialogCoworkWorkflowForm(props: {
  accounting?: boolean
  onSubmit: (value: { name: string; metadata: Record<string, string | number | boolean> }) => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const currentYear = new Date().getFullYear()
  const [store, setStore] = createStore({ name: "", workflowKind: "period" as "workflow" | "period", periodType: "monthly" as PeriodType, period: "january", year: currentYear })

  const setPeriodType = (periodType: PeriodType) => {
    setStore("periodType", periodType)
    setStore("period", periodOptions(periodType)[0])
  }

  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    const name = store.workflowKind === "period" ? periodName(store.periodType, store.period, store.year, language.t) : store.name.trim()
    if (!name) return
    dialog.close()
    props.onSubmit({
      name,
      metadata:
        store.workflowKind === "period"
          ? periodMetadata(props.accounting === true, store.periodType, store.period, store.year)
          : {},
    })
  }

  return (
    <Dialog fit>
      <form class="contents" onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>{language.t("cowork.workflow.createTitle")}</DialogTitle>
        </DialogHeader>
        <DividerV2 />
        <DialogBody class="flex w-96 flex-col gap-4 px-4 pt-4 pb-1">
          <Field>
            <Field.Label>{language.t("cowork.workflow.type")}</Field.Label>
            <select
              class="h-10 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-14-regular text-v2-text-text-base outline-none"
              value={store.workflowKind}
              onChange={(event) => setStore("workflowKind", event.currentTarget.value as "workflow" | "period")}
            >
              <option value="workflow">{language.t("cowork.workflow.type.workflow")}</option>
              <option value="period">{language.t("cowork.workflow.type.periodical")}</option>
            </select>
          </Field>
          <Show
            when={store.workflowKind === "period"}
            fallback={
              <Field>
                <Field.Label>{language.t("cowork.workflow.name")}</Field.Label>
                <TextInputV2
                  autofocus
                  appearance="large"
                  class="!w-full"
                  value={store.name}
                  placeholder={language.t("cowork.workflow.namePlaceholder")}
                  onInput={(event) => setStore("name", event.currentTarget.value)}
                />
              </Field>
            }
          >
            <Field>
              <Field.Label>{language.t("cowork.period.type")}</Field.Label>
              <select class="h-10 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-14-regular text-v2-text-text-base outline-none" value={store.periodType} onChange={(event) => setPeriodType(event.currentTarget.value as PeriodType)}>
                <option value="monthly">{language.t("cowork.period.type.monthly")}</option>
                <option value="quarterly">{language.t("cowork.period.type.quarterly")}</option>
                <option value="yearly">{language.t("cowork.period.type.yearly")}</option>
              </select>
            </Field>
            <Field>
              <Field.Label>{language.t("cowork.period.select")}</Field.Label>
              <select class="h-10 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-14-regular text-v2-text-text-base outline-none" value={store.period} onChange={(event) => setStore("period", event.currentTarget.value)}>
                <For each={periodOptions(store.periodType)}>{(option) => <option value={option}>{periodLabel(store.periodType, option, language.t)}</option>}</For>
              </select>
            </Field>
            <Field>
              <Field.Label>{language.t("cowork.period.year")}</Field.Label>
              <select class="h-10 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-14-regular text-v2-text-text-base outline-none" value={store.year} onChange={(event) => setStore("year", Number(event.currentTarget.value))}>
                <For each={periodYears(currentYear)}>{(year) => <option value={year}>{year}</option>}</For>
              </select>
            </Field>
          </Show>
        </DialogBody>
        <DialogFooter>
          <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </ButtonV2>
          <ButtonV2 type="submit" variant="contrast" disabled={store.workflowKind === "period" ? false : !store.name.trim()}>
            {language.t(store.workflowKind === "period" ? "cowork.period.create" : "cowork.workflow.create")}
          </ButtonV2>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

type PeriodType = "monthly" | "quarterly" | "yearly"

function periodOptions(type: PeriodType) {
  if (type === "quarterly") return ["q1", "q2", "q3", "q4"]
  if (type === "yearly") return ["year"]
  return ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"]
}

function periodYears(currentYear: number) {
  return [currentYear - 1, currentYear, currentYear + 1]
}

function periodLabel(type: PeriodType, period: string, translate: (key: string) => string) {
  if (type === "quarterly") return translate(`cowork.period.${period}`)
  if (type === "yearly") return translate("cowork.period.fullYear")
  return translate(`cowork.period.month.${period}`)
}

function periodName(type: PeriodType, period: string, year: number, translate: (key: string) => string) {
  return `${periodLabel(type, period, translate)} ${year}`
}

function periodMetadata(accounting: boolean, periodType: PeriodType, period: string, year: number): Record<string, string | number | boolean> {
  const metadata: Record<string, string | number | boolean> = {
    workflow_type: accounting ? "accounting_period" : "period",
    period_type: periodType,
    period,
    year,
    period_driven: true,
    accounting_enabled: accounting,
  }
  if (accounting) metadata.agent = "accounting-coworker"
  return metadata
}
